// src/components/UploadWorkspace/YTVideoProcessor.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getYoutubeWORKSPACEcaptions } from '../../../../myStudio/myBumpUp/myCreator/getVideoCaptions';
import { auth, db } from '../../../../firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation, faSpinner, faCircleNotch } from '@fortawesome/free-solid-svg-icons';
import VideoPreview from './VideoPreview';
import { extractYouTubeVideoId } from '../../youtubeLinks';
import link_icon from '../../../../assets/images/icons/link-icon.png';
import './linkWorkspace.css';

const YTVideoProcessor = ({
  workspaceVideoDetails,
  setWorkspaceVideoDetails,
  setDoneVideos,
  doneVideos,
  onInputChange,
  content_id,
  isTranscriptAvailable,
  isYTProcessing,
  isTranscriptError,
  onBeforeUpload,
  onAfterUpload,
}) => {
  const [workspaceInputUrl, setWorkspaceInputUrl] = useState('');
  const [isOverTimebank, setIsOverTimebank] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [istoLong, setIstoLong] = useState(false);
  const [isLiveOrProcessing, setIsLiveOrProcessing] = useState(false);
  const [noValidVideo, setNoValidVideo] = useState(false);
  const [showVideoNotFound, setShowVideoNotFound] = useState(false);

  const [processingStatus, setProcessingStatus] = useState({});
  const [videoProcessingError, setVideoProcessingError] = useState({});

  const [creditsUsed, setCreditsUsed] = useState(0);
  const [creditsLimit, setCreditsLimit] = useState(0);

  const extractVideoID = url => {
    const id = extractYouTubeVideoId(url);
    if (id) return id;
    const ws = /^.*(?:workspace\.com\/video\/)([^#&?]*).*/;
    const w = url.match(ws);
    if (w?.[1] && /^[\w-]{11}$/.test(w[1])) return w[1];
    return null;
  };

  const convertDurationToSeconds = duration => {
    if (!duration) return 0;
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    if (!match) return 0;
    const h = match[1] ? parseInt(match[1], 10) : 0;
    const m = match[2] ? parseInt(match[2], 10) : 0;
    const s = match[3] ? parseInt(match[3], 10) : 0;
    return h * 3600 + m * 60 + s;
  };

  const remainingCredits = Math.max(creditsLimit - creditsUsed, 0);

  // fetch subscription data
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const planRef = doc(db, 'users', user.uid, 'subscriptionData', 'planDetails');
    getDoc(planRef)
      .then(snap => {
        if (snap.exists()) {
          const d = snap.data();
          setCreditsUsed(d.creditsUsed ?? 0);
          setCreditsLimit(d.creditsLimit ?? 0);
        }
      })
      .catch(() => {});
    const unsub = onSnapshot(planRef, snap => {
      if (snap.exists()) {
        const d = snap.data();
        setCreditsUsed(d.creditsUsed ?? 0);
        setCreditsLimit(d.creditsLimit ?? 0);
      }
    });
    return () => unsub();
  }, []);

  // timebank check
  useEffect(() => {
    const dur = workspaceVideoDetails.durationSeconds != null
      ? workspaceVideoDetails.durationSeconds
      : convertDurationToSeconds(workspaceVideoDetails.duration);
    setIsOverTimebank(dur > remainingCredits * 60);
  }, [workspaceVideoDetails, remainingCredits]);

  // fetch video metadata
  const fetchWorkspaceVideoDetails = async url => {
    const videoId = extractVideoID(url);
    if (!videoId) {
      setNoValidVideo(true);
      setShowVideoNotFound(true);
      return;
    }
    setIsSearching(true);

    try {
      const { data } = await axios.get(
        'https://www.googleapis.com/youtube/v3/videos',
        {
          params: {
            part: 'snippet,contentDetails,status',
            id: videoId,
            key: process.env.REACT_APP_YOUTUBE_API_KEY,
          },
        }
      );

      if (!data.items.length) {
        setNoValidVideo(true);
        setShowVideoNotFound(true);
        setIsSearching(false);
        return;
      }

      const { snippet, contentDetails, status } = data.items[0];
      const durationSeconds = convertDurationToSeconds(contentDetails.duration);
      setIstoLong(durationSeconds > 10800);
      // Live streams, premieres, and just-ended streams still being processed come
      // back with liveBroadcastContent != 'none' and/or a zero (P0D) duration. Gemini
      // cannot ingest them, so block the upload button instead of charging the user.
      setIsLiveOrProcessing(
        (snippet.liveBroadcastContent && snippet.liveBroadcastContent !== 'none') || durationSeconds <= 0
      );

      const thumbnailUrl =
        snippet.thumbnails?.maxres?.url ||
        snippet.thumbnails?.standard?.url ||
        snippet.thumbnails?.high?.url;

      if (!thumbnailUrl) {
        setNoValidVideo(true);
        setShowVideoNotFound(true);
        setIsSearching(false);
        return;
      }

      setWorkspaceVideoDetails({
        thumbnailUrl,
        duration: contentDetails.duration,
        durationSeconds,
        language: snippet.defaultLanguage || 'Language unspecified',
        visibility: status.privacyStatus,
        videoId,
        title: snippet.title,
      });
      setIsSearching(false);
      onInputChange(true);
    } catch {
      setNoValidVideo(true);
      setShowVideoNotFound(true);
      setIsSearching(false);
    }
  };

  // handle input change
  const handleUrlChange = async url => {
    const id = extractVideoID(url);
    setWorkspaceInputUrl(url);
    setNoValidVideo(false);
    setIstoLong(false);
    setIsLiveOrProcessing(false);
    setIsOverTimebank(false);
    setShowVideoNotFound(false);

    if (id) {
      await fetchWorkspaceVideoDetails(url);
    } else {
      setWorkspaceVideoDetails({});
      setShowVideoNotFound(true);
      onInputChange(!!url.trim());
    }
  };

  // upload & processing logic
  const handleWorkspaceURLProcessing = async () => {
    const user = auth.currentUser;
    const videoId = workspaceVideoDetails.videoId;
    if (!videoId) {
      setShowVideoNotFound(true);
      return;
    }

    setIsSearching(true);
    setProcessingStatus(ps => ({ ...ps, [videoId]: true }));

    // Auto-create the workspace if we're on the new workspace page
    let effectiveContentId = content_id;
    if (!effectiveContentId && onBeforeUpload) {
      try {
        effectiveContentId = await onBeforeUpload();
      } catch {
        setIsSearching(false);
        setProcessingStatus(ps => ({ ...ps, [videoId]: false }));
        return;
      }
    }

    try {
      const { message } = await getYoutubeWORKSPACEcaptions({
        videoId,
        durationInSeconds:
          workspaceVideoDetails.durationSeconds ??
          convertDurationToSeconds(workspaceVideoDetails.duration),
        thumbnailUrl: workspaceVideoDetails.thumbnailUrl,
        title: workspaceVideoDetails.title,
        isReprocess: false,
        fullUrl: workspaceInputUrl,
        isPublic: workspaceVideoDetails.visibility === 'public',
        content_id: effectiveContentId,
        isWorkspace: true,
      });

      if (message === 'success') {
        const uid = user.uid;
        localStorage.removeItem(`videoCache_${uid}_WorkspaceVideos`);
        setProcessingStatus(ps => ({ ...ps, [videoId]: false }));
        setIsSearching(false);
        setDoneVideos(d => (d.includes(videoId) ? d : [...d, videoId]));
        // Navigate to the existing workspace page if we just auto-created it
        if (onAfterUpload && effectiveContentId && !content_id) {
          onAfterUpload(effectiveContentId);
        }
      } else {
        const errMsg =
          message === 'captions_not_available'
            ? 'Captions not available for this video.'
            : message === 'captions_not_available_in_language'
            ? 'Captions not available in your preferred language.'
            : 'Processing took longer than usual...';
        setVideoProcessingError(ve => ({ ...ve, [videoId]: errMsg }));
        setProcessingStatus(ps => ({ ...ps, [videoId]: false }));
        setIsSearching(false);
      }
    } catch {
      setVideoProcessingError(ve => ({
        ...ve,
        [workspaceVideoDetails.videoId]:
          'An internal error occurred during processing.',
      }));
      setProcessingStatus(ps => ({
        ...ps,
        [workspaceVideoDetails.videoId]: false,
      }));
      setIsSearching(false);
    }
  };

  // disable input when empty
  useEffect(() => {
    if (!workspaceInputUrl.trim()) onInputChange(false);
  }, [workspaceInputUrl, onInputChange]);

  return (
    <>
      {/* URL Input Section */}
      <section className="worklink-urlinput-section">
        <div className="worklink-urlinput-container">
          <div className="worklink-input-icon-wrapper">
            <img
              src={link_icon}
              alt="Link Icon"
              className="worklink-url-input-icon"
            />
            <input
              type="text"
              id="urlInput"
              className="worklink-url-input-box"
              placeholder={
                isYTProcessing && workspaceVideoDetails.videoId
                  ? `https://youtu.be/${workspaceVideoDetails.videoId}`
                  : 'Drop a YouTube link...'
              }
              value={workspaceInputUrl}
              onChange={e => handleUrlChange(e.target.value)}
              disabled={isYTProcessing || isTranscriptAvailable}
            />
            {/* Inline status icon on the right while processing */}
            {(processingStatus[workspaceVideoDetails.videoId] || isYTProcessing) && (
              isTranscriptError ? (
                <FontAwesomeIcon
                  icon={faTriangleExclamation}
                  className="worklink-inline-status-icon"
                  title="An error occurred – click to retry"
                />
              ) : (
                <FontAwesomeIcon
                  icon={faCircleNotch}
                  spin
                  className="worklink-inline-status-icon worklink-inline-spinner"
                />
              )
            )}
          </div>
          <div>
            {!processingStatus[workspaceVideoDetails.videoId] &&
              !doneVideos.includes(workspaceVideoDetails.videoId) &&
              !isOverTimebank &&
              !istoLong &&
              !isLiveOrProcessing &&
              !isYTProcessing && (
                <button
                  className={`worklink-url-input-button ${
                    !workspaceInputUrl || isSearching || noValidVideo
                      ? 'disabled'
                      : ''
                  }`}
                  onClick={handleWorkspaceURLProcessing}
                  disabled={
                    !workspaceInputUrl || isSearching || noValidVideo
                  }
                >
                  {noValidVideo
                    ? 'Not Valid URL'
                    : isSearching ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin />{' '}
                        Processing...
                      </>
                    ) : (
                      'Upload Video'
                    )}
                </button>
              )}

            {isOverTimebank && !doneVideos.includes(workspaceVideoDetails.videoId) && (
              <Link
                to="/settings/plan"
                className="worklink-url-input-button timebank-over"
              >
                Get More Time
              </Link>
            )}

            {istoLong && !doneVideos.includes(workspaceVideoDetails.videoId) && (
              <button
                className="worklink-url-input-button max-duration-warning"
                disabled
              >
                Max Duration 3h
              </button>
            )}

            {isLiveOrProcessing && !istoLong && !doneVideos.includes(workspaceVideoDetails.videoId) && (
              <button
                className="worklink-url-input-button max-duration-warning"
                disabled
                title="Live streams and videos still processing aren’t supported yet"
              >
                Live / Processing
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Video Preview Section */}
      {workspaceVideoDetails.thumbnailUrl && (
        <VideoPreview
          details={workspaceVideoDetails}
          isProcessing={isYTProcessing}
          errorMessage={videoProcessingError[workspaceVideoDetails.videoId]}
          done={doneVideos.includes(workspaceVideoDetails.videoId)}
          isTranscriptError={isTranscriptError}
          content_id={content_id}
        />
      )}

      {/* Video Not Found Message */}
      {showVideoNotFound && workspaceInputUrl && (
        <div className="worklink-video-not-found">
          <div>
            <div className="worklink-main-text">Video Not Found 🔎</div>
            <div className="worklink-small-text">
              Check your video id:
              <span className="unauth-example-url">
                https://www.youtube.com/watch?v=
                <span className="highlight-video-id">Vevu1P5XGNk</span>
              </span>
              <br />
              Supports videos from 4 to 180 minutes ⏲️
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default YTVideoProcessor;
